import { useEffect, useState, useMemo, Fragment } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Plus, Trash2, Send, Save, Eye, FileText, Mail, Building2, ExternalLink, FlaskConical, Repeat } from 'lucide-react'
import { supabase, isDemoMode } from '../lib/supabase'
import { DemoNotice } from '../lib/demo'
import { buildContext, render, pickTemplate } from '../lib/orderTemplates'
import { loadBrand } from '../lib/brand'
import { reorderFromOrder } from '../lib/reorder'
import SavedIndicator from '../components/SavedIndicator'

const PREVIEW_TABS = [
  { key: 'client_form',    label: 'Client form',    icon: FileText },
  { key: 'client_email',   label: 'Client email',   icon: Mail },
  { key: 'internal_email', label: 'Internal email', icon: Building2 },
]

function effectiveFromCardProduct(cp) {
  const p = cp.products || {}
  const o = cp.overrides || {}
  return {
    product_id: p.id,
    sku: p.sku,
    name: o.name ?? p.name,
    description: o.description ?? p.description,
    unit: o.unit ?? p.unit,
    pack_size: o.pack_size ?? p.pack_size ?? null,
    price: Number(o.price ?? p.price ?? 0),
    qty: cp.default_qty ?? 1,
  }
}

export default function OrderDetail({ clientMode = false }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'new'
  const backPath = clientMode ? '/my-orders' : '/orders'
  const backLabel = clientMode ? 'My orders' : 'All orders'

  const [companies, setCompanies] = useState([])
  const [cards, setCards] = useState([])
  const [allProducts, setAllProducts] = useState([])
  const [templates, setTemplates] = useState([])
  const [brand, setBrand] = useState(null)
  const [consignments, setConsignments] = useState([])
  const [originalConsignmentIds, setOriginalConsignmentIds] = useState([])

  const [order, setOrder] = useState(() => isNew ? {
    company_id: '', card_id: '',
    delivery_address: '', delivery_postcode: '',
    delivery_date_requested: '', customer_po: '',
    client_notes: '', internal_notes: '',
    status: 'draft',
  } : null)
  const [lines, setLines] = useState([])
  const [company, setCompany] = useState(null)

  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(0)
  const [sending, setSending] = useState(false)
  const [previewTab, setPreviewTab] = useState('client_form')
  const [editedTemplate, setEditedTemplate] = useState(null) // per-order edits
  const [addProductOpen, setAddProductOpen] = useState(false)
  // When the user clicks "+ Add product" inside a specific consignment header,
  // we remember which consignment the next product should land on.
  const [addProductForConsignment, setAddProductForConsignment] = useState(null)
  const [productSearch, setProductSearch] = useState('')
  const [reordering, setReordering] = useState(false)

  // Whether this order's client (via its group) wants the consignment flow.
  // Defaults to TRUE — only clients with an explicit group flag set to false
  // (e.g. Marstons pub chains) skip the "Hamper for / +New consignment" UI.
  const consignmentsEnabled = useMemo(() => {
    const active = companies.find(c => c.id === order?.company_id)
    const flag = active?.client_groups?.consignments_enabled
    return flag !== false // undefined / null / missing group → enabled
  }, [companies, order?.company_id])

  const handleReorder = async () => {
    if (reordering || !id || isNew) return
    setReordering(true)
    try {
      const { id: newId } = await reorderFromOrder(id)
      navigate(clientMode ? `/my-orders/${newId}` : `/orders/${newId}`)
    } catch (err) {
      alert(`Reorder failed: ${err.message || err}`)
      setReordering(false)
    }
  }

  // ---- Load reference data + order itself
  useEffect(() => {
    const init = async () => {
      if (isDemoMode) return
      const [companiesRes, productsRes, templatesRes, brandRes] = await Promise.all([
        supabase.from('companies').select('id, name, contact_name, contact_email, delivery_address, delivery_postcode, client_group_id, client_groups(id, name, consignments_enabled)').is('archived_at', null).order('name'),
        supabase.from('products').select('id, sku, name, description, unit, pack_size, price').is('archived_at', null).order('name'),
        supabase.from('order_templates').select('*'),
        loadBrand(),
      ])
      setCompanies(companiesRes.data || [])
      setAllProducts(productsRes.data || [])
      setTemplates(templatesRes.data || [])
      setBrand(brandRes)

      if (!isNew) {
        const { data: o } = await supabase
          .from('orders')
          .select('*, companies(id, name, contact_name, contact_email)')
          .eq('id', id).maybeSingle()
        if (o) {
          setOrder(o)
          setCompany(o.companies)
        }
        const { data: ls } = await supabase
          .from('order_lines').select('*').eq('order_id', id).order('line_number')
        setLines(ls || [])
        const { data: cs } = await supabase
          .from('order_consignments').select('*').eq('order_id', id).order('sort_order')
        setConsignments(cs || [])
        setOriginalConsignmentIds((cs || []).map(c => c.id))
      }
    }
    init()
  }, [id])

  // ---- Load cards when company changes (new orders only)
  useEffect(() => {
    if (!isNew || !order?.company_id) { setCards([]); return }
    if (isDemoMode) return
    supabase.from('cards').select('id, name').eq('company_id', order.company_id).is('archived_at', null).order('name')
      .then(({ data }) => setCards(data || []))
    const c = companies.find(c => c.id === order.company_id)
    setCompany(c || null)
    // Pre-fill delivery from the company if blank.
    setOrder(o => ({
      ...o,
      delivery_address: o.delivery_address || c?.delivery_address || '',
      delivery_postcode: o.delivery_postcode || c?.delivery_postcode || '',
    }))
  }, [order?.company_id, companies, isNew])

  // ---- Card load: when picking a card, pull its products as suggested lines
  const loadFromCard = async (cardId) => {
    setOrder(o => ({ ...o, card_id: cardId }))
    if (!cardId || isDemoMode) return
    const { data: cps } = await supabase
      .from('card_products')
      .select('id, overrides, default_qty, sort_order, products(id, sku, name, description, unit, pack_size, price)')
      .eq('card_id', cardId)
      .order('sort_order')
    const newLines = (cps || []).map((cp, i) => {
      const eff = effectiveFromCardProduct(cp)
      return {
        product_id: eff.product_id,
        line_number: i + 1,
        sku_at_order: eff.sku,
        name_at_order: eff.name,
        description_at_order: eff.description,
        unit_at_order: eff.unit,
        pack_size_at_order: eff.pack_size,
        price_at_order: eff.price,
        qty: eff.qty,
      }
    })
    setLines(newLines)
  }

  const addLineFromProduct = (product) => {
    setLines(ls => [...ls, {
      product_id: product.id,
      line_number: ls.length + 1,
      sku_at_order: product.sku,
      name_at_order: product.name,
      description_at_order: product.description,
      unit_at_order: product.unit,
      pack_size_at_order: product.pack_size ?? null,
      price_at_order: Number(product.price ?? 0),
      qty: 1,
      consignment_id: addProductForConsignment || null,
    }])
    setProductSearch('')
    setAddProductOpen(false)
    setAddProductForConsignment(null)
  }

  const updateLine = (idx, patch) => {
    setLines(ls => ls.map((l, i) => i === idx ? { ...l, ...patch } : l))
  }
  const removeLine = (idx) => {
    setLines(ls => ls.filter((_, i) => i !== idx).map((l, i) => ({ ...l, line_number: i + 1 })))
  }

  // ---- Consignment editing (staff)
  const isTempConsId = (cid) => typeof cid === 'string' && cid.startsWith('temp-')

  const addConsignment = () => {
    const newId = 'temp-' + Math.random().toString(36).slice(2, 12)
    setConsignments(cs => [...cs, {
      id: newId, recipient_name: '', recipient_ref: '', sort_order: cs.length,
    }])
    return newId
  }

  // Auto-create a default consignment on new orders so the recipient inputs
  // are visible immediately and staff can start adding products to it.
  useEffect(() => {
    if (isNew && consignments.length === 0) {
      addConsignment()
    }
  }, [isNew])

  // Open the global product picker but scope the next pick to a specific consignment.
  const openPickerForConsignment = (consId) => {
    setAddProductForConsignment(consId)
    setAddProductOpen(true)
    setProductSearch('')
  }

  const removeConsignment = (consId) => {
    const linesInIt = lines.filter(l => l.consignment_id === consId).length
    const msg = linesInIt > 0
      ? `Remove this consignment AND its ${linesInIt} line(s)?`
      : 'Remove this consignment?'
    if (!confirm(msg)) return
    setConsignments(cs => cs.filter(c => c.id !== consId))
    setLines(ls => ls.filter(l => l.consignment_id !== consId))
  }

  const moveLineTo = (lineIdx, newConsignmentId) => {
    setLines(ls => ls.map((l, i) => i === lineIdx ? { ...l, consignment_id: newConsignmentId || null } : l))
  }

  const updateConsignment = (consId, patch) =>
    setConsignments(cs => cs.map(c => c.id === consId ? { ...c, ...patch } : c))

  const productCandidates = useMemo(() => {
    const s = productSearch.toLowerCase()
    return allProducts.filter(p =>
      !s || p.name.toLowerCase().includes(s) || p.sku.toLowerCase().includes(s)
    ).slice(0, 20)
  }, [allProducts, productSearch])

  const total = useMemo(
    () => lines.reduce((s, l) => s + Number(l.price_at_order || 0) * Number(l.qty || 0), 0),
    [lines]
  )

  // ---- Save
  const save = async () => {
    if (isDemoMode) return
    if (!order.company_id) return alert('Pick a company first.')
    setSaving(true)
    try {
      const payload = {
        company_id: order.company_id,
        card_id: order.card_id || null,
        status: order.status || 'draft',
        delivery_address: order.delivery_address || null,
        delivery_postcode: order.delivery_postcode || null,
        delivery_date_requested: order.delivery_date_requested || null,
        customer_po: order.customer_po || null,
        client_notes: order.client_notes || null,
        internal_notes: order.internal_notes || null,
      }
      let orderId = id
      if (isNew) {
        const { data, error } = await supabase.from('orders').insert(payload).select().single()
        if (error) throw error
        orderId = data.id
      } else {
        const { error } = await supabase.from('orders').update(payload).eq('id', id)
        if (error) throw error
      }
      // 1. Delete consignments removed in the editor (cascades their order_lines too).
      const stillInState = new Set(consignments.map(c => c.id))
      const deletedIds = originalConsignmentIds.filter(oid => !stillInState.has(oid))
      if (deletedIds.length > 0) {
        const { error } = await supabase.from('order_consignments').delete().in('id', deletedIds)
        if (error) throw error
      }

      // 2. Update existing consignments + insert new ones; build tempId -> realId map.
      const tempIdMap = {}
      let i = 0
      for (const c of consignments) {
        if (isTempConsId(c.id)) {
          const { data: inserted, error } = await supabase.from('order_consignments').insert({
            order_id: orderId,
            recipient_name: c.recipient_name || null,
            recipient_ref:  c.recipient_ref  || null,
            sort_order: i,
          }).select().single()
          if (error) throw error
          tempIdMap[c.id] = inserted.id
        } else {
          const { error } = await supabase.from('order_consignments').update({
            recipient_name: c.recipient_name || null,
            recipient_ref:  c.recipient_ref  || null,
            sort_order: i,
          }).eq('id', c.id)
          if (error) throw error
        }
        i++
      }

      // 3. Rewrite lines (delete + insert), mapping temp consignment ids to their real ids.
      await supabase.from('order_lines').delete().eq('order_id', orderId)
      if (lines.length > 0) {
        const linesToInsert = lines.map((l, i) => ({
          order_id: orderId,
          consignment_id: l.consignment_id
            ? (tempIdMap[l.consignment_id] || (isTempConsId(l.consignment_id) ? null : l.consignment_id))
            : null,
          product_id: l.product_id || null,
          line_number: i + 1,
          sku_at_order: l.sku_at_order,
          name_at_order: l.name_at_order,
          description_at_order: l.description_at_order,
          unit_at_order: l.unit_at_order,
          pack_size_at_order: l.pack_size_at_order ?? null,
          price_at_order: Number(l.price_at_order || 0),
          qty: Number(l.qty || 0),
          recipient_name: l.recipient_name || null,
          recipient_ref:  l.recipient_ref  || null,
        }))
        const { error } = await supabase.from('order_lines').insert(linesToInsert)
        if (error) throw error
      }

      // 4. Refresh state so subsequent edits operate on real ids.
      const { data: freshCs } = await supabase
        .from('order_consignments').select('*').eq('order_id', orderId).order('sort_order')
      setConsignments(freshCs || [])
      setOriginalConsignmentIds((freshCs || []).map(c => c.id))
      if (isNew) navigate(`/orders/${orderId}`, { replace: true })
      else setSavedAt(Date.now())
    } catch (err) {
      alert(err.message)
    } finally {
      setSaving(false)
    }
  }

  // ---- Render templates
  const formTemplate    = pickTemplate(templates, 'client_form',    order?.company_id)
  const clientTemplate  = pickTemplate(templates, 'client_email',   order?.company_id)
  const internalTemplate = pickTemplate(templates, 'internal_email', order?.company_id)

  const ctxBase = brand && order ? buildContext({
    order, company, brand, lines, formHtml: '',
  }) : null

  const renderedForm = ctxBase && formTemplate
    ? render(editedTemplate?.kind === 'client_form' ? editedTemplate.body_html : formTemplate.body_html, ctxBase)
    : ''

  const ctxWithForm = ctxBase ? { ...ctxBase, formHtml: renderedForm } : null

  const renderedClient = ctxWithForm && clientTemplate
    ? render(editedTemplate?.kind === 'client_email' ? editedTemplate.body_html : clientTemplate.body_html, ctxWithForm)
    : ''
  const renderedClientSubject = ctxWithForm && clientTemplate
    ? render(clientTemplate.subject || '', ctxWithForm) : ''

  const renderedInternal = ctxWithForm && internalTemplate
    ? render(editedTemplate?.kind === 'internal_email' ? editedTemplate.body_html : internalTemplate.body_html, ctxWithForm)
    : ''
  const renderedInternalSubject = ctxWithForm && internalTemplate
    ? render(internalTemplate.subject || '', ctxWithForm) : ''

  const previewHtml =
    previewTab === 'client_form'    ? renderedForm
    : previewTab === 'client_email' ? renderedClient
    : renderedInternal
  const previewSubject =
    previewTab === 'client_email'   ? renderedClientSubject
    : previewTab === 'internal_email' ? renderedInternalSubject
    : null

  const currentTemplate = templates.find(t =>
    t.kind === previewTab &&
    ((t.scope === 'company' && t.company_id === order?.company_id) || t.scope === 'global')
  )
  const currentBody = editedTemplate?.kind === previewTab
    ? editedTemplate.body_html
    : currentTemplate?.body_html || ''

  const onTemplateEdit = (body) => {
    setEditedTemplate({ kind: previewTab, body_html: body })
  }

  const saveTemplateAsDefault = async () => {
    if (isDemoMode || !editedTemplate) return
    if (!confirm('Save this as the global default template for all future orders?')) return
    const existing = templates.find(t => t.kind === editedTemplate.kind && t.scope === 'global')
    if (existing) {
      await supabase.from('order_templates').update({ body_html: editedTemplate.body_html }).eq('id', existing.id)
    } else {
      await supabase.from('order_templates').insert({
        scope: 'global', kind: editedTemplate.kind, body_html: editedTemplate.body_html,
      })
    }
    const { data } = await supabase.from('order_templates').select('*')
    setTemplates(data || [])
    setEditedTemplate(null)
  }

  // ---- Send
  // mode = 'live' | 'test'
  // 'test'  -> always diverts to brand.test_email (test button)
  // 'live'  -> sends to real recipients UNLESS brand.test_mode is on,
  //            in which case it also diverts to test_email (safety net)
  const send = async (mode = 'live') => {
    if (isDemoMode || isNew) return

    const isTest = mode === 'test' || brand?.test_mode === true
    const testEmail = brand?.test_email
    if (isTest && !testEmail) {
      return alert('Test mode is on (or you clicked Send Test) but no test email is configured. Open Settings → Branding to set one.')
    }
    if (!isTest && !company?.contact_email) {
      if (!confirm('No client contact email on the company. Send internal only?')) return
    }

    if (!isTest) {
      if (!confirm(`Send live to ${company?.contact_email || '(internal only)'}?`)) return
    }

    setSending(true)
    try {
      const subjectPrefix = isTest ? '[TEST] ' : ''
      const internalTo = (brand?.internal_recipients || '')
        .split(',').map(s => s.trim()).filter(Boolean)
      const payload = {
        order_id: id,
        from: brand?.from_email || null,
        reply_to: brand?.reply_to_email || null,
        bcc: brand?.bcc_email || null,
        client: company?.contact_email || isTest ? {
          to: company?.contact_email || testEmail,
          subject: subjectPrefix + renderedClientSubject,
          html: renderedClient,
        } : null,
        internal: internalTo.length > 0 || isTest ? {
          to: internalTo,
          subject: subjectPrefix + renderedInternalSubject,
          html: renderedInternal,
        } : null,
        test: isTest ? { redirect_to: testEmail } : null,
      }
      let sent = false
      try {
        const { error } = await supabase.functions.invoke('send-order-email', { body: payload })
        if (error) throw error
        sent = true
      } catch (err) {
        // Edge function not deployed or Resend not configured -- fall back to mailto.
        console.warn('Edge function unavailable, falling back to mailto:', err?.message)
        const target = isTest ? testEmail : (company?.contact_email || '')
        const subj = encodeURIComponent(subjectPrefix + (renderedClientSubject || `Order ${order.reference}`))
        const body = encodeURIComponent(`View this order online: ${window.location.origin}/v/${order.public_token}`)
        window.location.href = `mailto:${target}?subject=${subj}&body=${body}`
      }
      // Only mark the order as 'sent' on a real send -- test sends shouldn't touch status.
      if (!isTest) {
        await supabase.from('orders').update({
          status: 'sent',
          placed_at: new Date().toISOString(),
        }).eq('id', id)
        setOrder(o => ({ ...o, status: 'sent', placed_at: new Date().toISOString() }))
      }
      if (sent) alert(isTest ? `Test sent to ${testEmail}.` : 'Sent.')
    } catch (err) {
      alert(err.message)
    } finally {
      setSending(false)
    }
  }

  const openClientView = () => {
    if (!order?.public_token) return
    window.open(`/v/${order.public_token}`, '_blank', 'noopener')
  }

  if (!order) return <div className="card"><p className="text-sm text-gray-500">Loading…</p></div>

  return (
    <div className="space-y-4">
      <DemoNotice />

      <Link to={backPath} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft className="w-4 h-4" /> {backLabel}
      </Link>

      {/* Header */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-800">
              {isNew ? 'New order' : order.reference || 'Order'}
            </h3>
            {!isNew && order.status && (
              <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                {order.status}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            {!isNew && order?.status && order.status !== 'draft' && (
              <button
                onClick={handleReorder}
                disabled={reordering}
                title="Create a new draft with the same items"
                className="btn-secondary text-sm"
              >
                <Repeat className="w-4 h-4" />
                {reordering ? 'Copying…' : 'Reorder'}
              </button>
            )}
            {!clientMode && (
              <>
                <button onClick={save} disabled={saving} className="btn-secondary text-sm">
                  <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
                </button>
                <SavedIndicator savedAt={savedAt} />
              </>
            )}
            {!clientMode && !isNew && order?.public_token && (
              <button onClick={openClientView} className="btn-secondary text-sm">
                <ExternalLink className="w-4 h-4" /> View as client
              </button>
            )}
            {!clientMode && !isNew && brand?.test_email && (
              <button onClick={() => send('test')} disabled={sending} className="btn-secondary text-sm">
                <FlaskConical className="w-4 h-4" /> {sending ? 'Sending…' : 'Send test'}
              </button>
            )}
            {!clientMode && !isNew && (
              <button onClick={() => send('live')} disabled={sending} className="btn-primary text-sm">
                <Send className="w-4 h-4" />
                {sending ? 'Sending…' : (brand?.test_mode ? 'Send (test mode)' : 'Send live')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Order details */}
      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-3">Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label>
            <span className="text-xs text-gray-500 block mb-1">Company</span>
            <select
              value={order.company_id || ''}
              onChange={e => setOrder({ ...order, company_id: e.target.value, card_id: '' })}
              className="input-field"
              disabled={!isNew}
            >
              <option value="">— select —</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          {isNew && cards.length > 0 && (
            <label>
              <span className="text-xs text-gray-500 block mb-1">Load lines from card (optional)</span>
              <select value={order.card_id || ''} onChange={e => loadFromCard(e.target.value)} className="input-field">
                <option value="">— none —</option>
                {cards.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          )}
          <label>
            <span className="text-xs text-gray-500 block mb-1">Customer PO</span>
            <input type="text" value={order.customer_po || ''} onChange={e => setOrder({ ...order, customer_po: e.target.value })} className="input-field" />
          </label>
          <label>
            <span className="text-xs text-gray-500 block mb-1">Requested delivery date</span>
            <input type="date" value={order.delivery_date_requested || ''} onChange={e => setOrder({ ...order, delivery_date_requested: e.target.value })} className="input-field" />
          </label>
          {(() => {
            const active = companies.find(c => c.id === order.company_id)
            const groupId = active?.client_group_id
            const groupSites = groupId ? companies.filter(c => c.client_group_id === groupId && c.delivery_address) : []
            const showPicker = groupSites.length > 0
            return showPicker && (
              <label className="sm:col-span-2">
                <span className="text-xs text-gray-500 block mb-1">Use address from…</span>
                <select
                  value=""
                  onChange={e => {
                    const src = companies.find(c => c.id === e.target.value)
                    if (!src) return
                    setOrder({
                      ...order,
                      delivery_address: src.delivery_address || '',
                      delivery_postcode: src.delivery_postcode || '',
                    })
                    e.target.value = ''
                  }}
                  className="input-field"
                >
                  <option value="">— pick a site to auto-fill the address below —</option>
                  {groupSites.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.delivery_postcode ? ` · ${c.delivery_postcode}` : ''}
                    </option>
                  ))}
                </select>
              </label>
            )
          })()}
          <label className="sm:col-span-2">
            <span className="text-xs text-gray-500 block mb-1">Delivery address</span>
            <textarea value={order.delivery_address || ''} onChange={e => setOrder({ ...order, delivery_address: e.target.value })} className="input-field" rows={2} />
          </label>
          <label>
            <span className="text-xs text-gray-500 block mb-1">Delivery postcode</span>
            <input type="text" value={order.delivery_postcode || ''} onChange={e => setOrder({ ...order, delivery_postcode: e.target.value })} className="input-field" />
          </label>
          <label className="sm:col-span-2">
            <span className="text-xs text-gray-500 block mb-1">Notes to client (appear on the form/email)</span>
            <textarea value={order.client_notes || ''} onChange={e => setOrder({ ...order, client_notes: e.target.value })} className="input-field" rows={2} />
          </label>
          <label className="sm:col-span-2">
            <span className="text-xs text-gray-500 block mb-1">Internal notes (not sent to client)</span>
            <textarea value={order.internal_notes || ''} onChange={e => setOrder({ ...order, internal_notes: e.target.value })} className="input-field" rows={2} />
          </label>
        </div>
      </div>

      {/* Lines */}
      <div className="card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-semibold text-gray-800">Lines</h3>
          {consignmentsEnabled ? (
            <button onClick={() => openPickerForConsignment(addConsignment())} className="btn-primary text-sm">
              <Plus className="w-4 h-4" /> New consignment
            </button>
          ) : (
            <button onClick={() => openPickerForConsignment(null)} className="btn-primary text-sm">
              <Plus className="w-4 h-4" /> Add product
            </button>
          )}
        </div>

        {addProductOpen && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <input
              type="text" value={productSearch} onChange={e => setProductSearch(e.target.value)}
              placeholder="Search products to add…" className="input-field" autoFocus
            />
            {productCandidates.length > 0 && (
              <ul className="mt-2 divide-y divide-gray-100 bg-white rounded-lg border border-gray-100 max-h-72 overflow-auto">
                {productCandidates.map(p => (
                  <li key={p.id}>
                    <button type="button" onClick={() => addLineFromProduct(p)} className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-gray-50 text-left">
                      <div>
                        <div className="text-sm font-medium text-gray-800">{p.name}</div>
                        <div className="text-xs text-gray-500">{p.sku}</div>
                      </div>
                      {p.price != null && <div className="text-sm text-gray-700">£{Number(p.price).toFixed(2)}</div>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {lines.length === 0 && consignments.length === 0
          ? <p className="text-sm text-gray-500 py-6 text-center">No lines yet.</p>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase text-gray-500 border-b border-gray-100">
                    <th className="text-left py-2">Product</th>
                    <th className="text-right py-2 w-24">Qty</th>
                    <th className="text-right py-2 w-28">Price</th>
                    <th className="text-right py-2 w-28">Total</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Build display order: walk lines in order, inserting a
                    // consignment header row when consignment_id changes.
                    const rows = []
                    let lastCons = '__init__'
                    let consNum = 0
                    lines.forEach((l, i) => {
                      const cId = l.consignment_id || null
                      if (cId !== lastCons) {
                        lastCons = cId
                        if (cId) consNum++
                        const cons = consignments.find(c => c.id === cId)
                        rows.push(
                          <tr key={`h-${cId || 'none'}-${i}`} className="bg-brand-50/40">
                            <td colSpan={5} className="py-2 px-2">
                              {cId ? (
                                <div className="flex gap-2 items-center flex-wrap">
                                  <span className="text-xs uppercase tracking-wide text-brand-dark font-semibold">
                                    Consignment {consNum}
                                  </span>
                                  <input
                                    type="text"
                                    value={cons?.recipient_name || ''}
                                    onChange={e => updateConsignment(cId, { recipient_name: e.target.value })}
                                    placeholder="Hamper for?"
                                    className="input-field text-sm py-1 max-w-xs"
                                  />
                                  <input
                                    type="text"
                                    value={cons?.recipient_ref || ''}
                                    onChange={e => updateConsignment(cId, { recipient_ref: e.target.value })}
                                    placeholder="No."
                                    className="input-field text-sm py-1 w-32"
                                  />
                                  <button
                                    onClick={() => openPickerForConsignment(cId)}
                                    className="btn-secondary text-xs py-1 px-2"
                                  >
                                    <Plus className="w-3.5 h-3.5" /> Add product
                                  </button>
                                  <button
                                    onClick={() => removeConsignment(cId)}
                                    className="ml-auto text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                                    title="Remove this consignment"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" /> Remove
                                  </button>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-500 italic">Ungrouped lines</span>
                              )}
                            </td>
                          </tr>
                        )
                      }
                      rows.push(
                        <tr key={i} className="border-b border-gray-50">
                          <td className="py-2 pr-2">
                            <input
                              type="text" value={l.name_at_order || ''}
                              onChange={e => updateLine(i, { name_at_order: e.target.value })}
                              className="input-field text-sm py-1.5"
                            />
                            <div className="text-xs text-gray-500 mt-0.5">
                              {l.sku_at_order}
                              {l.unit_at_order ? ` · ${l.unit_at_order}` : ''}
                              {l.pack_size_at_order ? ` · ${l.pack_size_at_order}/box` : ''}
                            </div>
                            {consignments.length > 0 && consignmentsEnabled && (
                              <div className="mt-1">
                                <label className="text-[10px] uppercase tracking-wide text-gray-400 mr-1">In:</label>
                                <select
                                  value={l.consignment_id || ''}
                                  onChange={e => moveLineTo(i, e.target.value === '__new__' ? (() => { const id = 'temp-' + Math.random().toString(36).slice(2, 12); setConsignments(cs => [...cs, { id, recipient_name: '', recipient_ref: '', sort_order: cs.length }]); return id })() : (e.target.value || null))}
                                  className="input-field text-xs py-1 w-auto inline-block"
                                >
                                  <option value="">— ungrouped —</option>
                                  {consignments.map((c, idx) => (
                                    <option key={c.id} value={c.id}>
                                      Consignment {idx + 1}{c.recipient_name ? ` — ${c.recipient_name}` : ''}{c.recipient_ref ? ` (No. ${c.recipient_ref})` : ''}
                                    </option>
                                  ))}
                                  <option value="__new__">+ New consignment…</option>
                                </select>
                              </div>
                            )}
                          </td>
                          <td className="py-2 pr-2 text-right">
                            <input
                              type="number" min="1" step="1"
                              value={l.qty}
                              onChange={e => {
                                const v = e.target.value
                                updateLine(i, { qty: v === '' ? '' : Math.max(1, parseInt(v, 10) || 1) })
                              }}
                              className="input-field text-sm py-1.5 text-right"
                            />
                          </td>
                          <td className="py-2 pr-2 text-right">
                            <input type="number" step="0.01" value={l.price_at_order} onChange={e => updateLine(i, { price_at_order: e.target.value })} className="input-field text-sm py-1.5 text-right" />
                          </td>
                          <td className="py-2 pr-2 text-right font-medium">
                            £{(Number(l.price_at_order || 0) * Number(l.qty || 0)).toFixed(2)}
                          </td>
                          <td className="py-2 text-right">
                            <button onClick={() => removeLine(i)} className="text-gray-400 hover:text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      )
                    })
                    // Show empty consignments (with no lines yet) so staff can fill them in.
                    const usedConsIds = new Set(lines.map(l => l.consignment_id).filter(Boolean))
                    consignments.filter(c => !usedConsIds.has(c.id)).forEach((c, idx) => {
                      rows.push(
                        <tr key={`empty-${c.id}`} className="bg-brand-50/40">
                          <td colSpan={5} className="py-2 px-2">
                            <div className="flex gap-2 items-center flex-wrap">
                              <span className="text-xs uppercase tracking-wide text-brand-dark font-semibold">
                                Consignment {idx + 1}
                              </span>
                              <input
                                type="text"
                                value={c.recipient_name || ''}
                                onChange={e => updateConsignment(c.id, { recipient_name: e.target.value })}
                                placeholder="Hamper for?"
                                className="input-field text-sm py-1 max-w-xs"
                              />
                              <input
                                type="text"
                                value={c.recipient_ref || ''}
                                onChange={e => updateConsignment(c.id, { recipient_ref: e.target.value })}
                                placeholder="No."
                                className="input-field text-sm py-1 w-32"
                              />
                              <button
                                onClick={() => openPickerForConsignment(c.id)}
                                className="btn-primary text-xs py-1 px-2"
                              >
                                <Plus className="w-3.5 h-3.5" /> Add product
                              </button>
                              <button
                                onClick={() => removeConsignment(c.id)}
                                className="ml-auto text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                              >
                                <Trash2 className="w-3.5 h-3.5" /> Remove
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })
                    return rows
                  })()}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} className="py-3 text-right font-medium text-gray-700">Total</td>
                    <td className="py-3 text-right font-semibold">£{total.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )
        }
      </div>

      {/* Preview + template edit -- staff only */}
      {!isNew && !clientMode && (
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Eye className="w-5 h-5 text-brand" /> Preview &amp; edit templates
          </h3>
          <div className="flex gap-1 border-b border-gray-200 mb-3 overflow-x-auto">
            {PREVIEW_TABS.map(t => (
              <button
                key={t.key}
                onClick={() => { setPreviewTab(t.key); setEditedTemplate(null) }}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  previewTab === t.key
                    ? 'border-brand text-brand-dark'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>
          {previewSubject != null && (
            <div className="mb-3 text-sm">
              <span className="text-xs text-gray-500 uppercase tracking-wide">Subject</span>
              <div className="font-medium text-gray-800">{previewSubject}</div>
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <span className="text-xs text-gray-500 uppercase tracking-wide block mb-1 flex items-center justify-between">
                <span>Template (HTML — edit freely)</span>
                {editedTemplate?.kind === previewTab && (
                  <button onClick={saveTemplateAsDefault} className="text-brand-dark hover:underline normal-case tracking-normal">
                    Save as default
                  </button>
                )}
              </span>
              <textarea
                value={currentBody}
                onChange={e => onTemplateEdit(e.target.value)}
                className="input-field font-mono text-xs"
                style={{ minHeight: 360 }}
              />
              <p className="text-xs text-gray-400 mt-1">
                Tokens: <code>{'{{order.reference}}'}</code>, <code>{'{{company.name}}'}</code>, <code>{'{{lines}}'}</code>, <code>{'{{form}}'}</code>, <code>{'{{brand.name}}'}</code>, etc.
              </p>
            </div>
            <div>
              <span className="text-xs text-gray-500 uppercase tracking-wide block mb-1">Preview</span>
              <div className="border border-gray-200 rounded-lg bg-white overflow-hidden" style={{ minHeight: 360 }}>
                <iframe srcDoc={previewHtml} title="preview" className="w-full h-full" style={{ minHeight: 360, border: 0 }} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
